/**
 * Superadmin-only facility trial list and creation entrypoint.
 */

import { useAuth } from "@risalabs_frontend_org/auth-wrapper";
import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import type { ProtocolTrial } from "../api/clinicalTrialsApiTypes";

/**
 * List and create facility-owned trials for Protocol View.
 *
 * @returns Protocols page element or redirect for non-superadmins
 */
export function ProtocolsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trials, setTrials] = useState<ProtocolTrial[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState("BNT327-06");
  const [sponsorProtocolId, setSponsorProtocolId] = useState("BNT327-06");
  const [creating, setCreating] = useState(false);

  const isSuperAdmin = Boolean(user?.isSuperAdmin);

  const loadTrials = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await clinicalTrialsService.listTrials(true);
      setTrials(response.trials);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load trials");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      void loadTrials();
    }
  }, [isSuperAdmin, loadTrials]);

  if (!isSuperAdmin) {
    return <Navigate to="/notifications" replace />;
  }

  /**
   * Create a trial after browser confirmation.
   *
   * @returns Nothing
   */
  const handleCreate = async () => {
    if (
      !window.confirm(
        `Create trial ${sponsorProtocolId} in the active facility?`,
      )
    ) {
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const trial = await clinicalTrialsService.createTrial({
        confirmation: true,
        display_name: displayName,
        sponsor_protocol_id: sponsorProtocolId,
      });
      await loadTrials();
      navigate(`/protocols/${trial.trial_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create trial");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Protocols</h1>
          <p className="mt-1 text-sm text-slate-600">
            Superadmin-only facility trial authoring and CSV ingestion.
          </p>
        </div>
      </div>

      <section className="mb-8 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-lg font-medium">Create trial</h2>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="text-sm">
            Display name
            <input
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </label>
          <label className="text-sm">
            Sponsor protocol ID
            <input
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={sponsorProtocolId}
              onChange={(event) => setSponsorProtocolId(event.target.value)}
            />
          </label>
          <div className="flex items-end">
            <button
              className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              onClick={() => void handleCreate()}
              disabled={creating || !displayName || !sponsorProtocolId}
            >
              {creating ? "Creating…" : "Create trial"}
            </button>
          </div>
        </div>
      </section>

      {error ? (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3 text-sm font-medium">
          Facility trials {loading ? "(loading…)" : `(${trials.length})`}
        </div>
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Protocol</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Latest draft</th>
              <th className="px-4 py-2">Active version</th>
            </tr>
          </thead>
          <tbody>
            {trials.map((trial) => (
              <tr
                key={trial.trial_id}
                className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                onClick={() => navigate(`/protocols/${trial.trial_id}`)}
              >
                <td className="px-4 py-3 font-medium text-slate-900">
                  {trial.display_name}
                </td>
                <td className="px-4 py-3">{trial.sponsor_protocol_id}</td>
                <td className="px-4 py-3">{trial.site_status}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  {trial.latest_draft_id || "—"}
                </td>
                <td className="px-4 py-3 font-mono text-xs">
                  {trial.active_protocol_version_id || "—"}
                </td>
              </tr>
            ))}
            {!loading && trials.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-slate-500" colSpan={5}>
                  No trials yet. Create one and import the BNT327-06 atoms CSV.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>
    </div>
  );
}
